// MedicalTestInvoicePdf.tsx
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";

/* ---------------- Fonts ---------------- */
Font.register({
  family: "OpenSans-Regular",
  src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf",
});
Font.register({
  family: "OpenSans-Bold",
  src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-700.ttf",
});
Font.register({
  family: "NotoSans",
  src: "https://fonts.gstatic.com/s/notosans/v27/o-0IIpQlx3QUlC5A4PNb4g.ttf",
});

/* ---------------- Styles ---------------- */
const styles = StyleSheet.create({
  page: {
    padding: 36,
    fontSize: 10,
    fontFamily: "OpenSans-Regular",
    color: "#222",
    lineHeight: 1.4,
  },

  /* ── Header ── */
  header: {
    alignItems: "center",
    marginBottom: 10,
  },
  clinicTitle: {
    fontFamily: "OpenSans-Bold",
    fontSize: 18,
    color: "#111",
    textAlign: "center",
  },
  clinicMeta: {
    fontSize: 8.5,
    color: "#555",
    textAlign: "center",
    marginTop: 3,
  },

  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#bbb",
    marginVertical: 10,
  },

  thinDivider: {
    borderBottomWidth: 1,
    borderBottomColor: "#e5e5e5",
    marginVertical: 8,
  },

  /* ── Receipt row ── */
  receiptRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  receiptLabel: {
    fontSize: 9,
    color: "#555",
  },
  receiptValue: {
    fontFamily: "OpenSans-Bold",
    fontSize: 9,
    color: "#111",
  },

  /* ── Patient / Doctor info ── */
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  infoCol: {
    width: "48%",
  },
  labelValueRow: {
    flexDirection: "row",
    marginBottom: 4,
    alignItems: "flex-start",
  },
  infoLabel: {
    fontSize: 9,
    color: "#555",
    marginRight: 4,
  },
  infoValue: {
    fontFamily: "OpenSans-Bold",
    fontSize: 9,
    color: "#111",
    flex: 1,
  },

  /* ── Services table ── */
  table: {
    marginTop: 8,
    width: "100%",
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#bbb",
    paddingBottom: 5,
    marginBottom: 2,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#ebebeb",
  },
  colSrNo: { width: "8%", fontSize: 9, color: "#333" },
  colDesc: { width: "70%", fontSize: 9, color: "#333" },
  colAmount: { width: "22%", textAlign: "right", fontSize: 9, color: "#333" },
  colHeaderText: {
    fontFamily: "OpenSans-Bold",
    fontSize: 8.5,
    color: "#555",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },

  /* ── Totals ── */
  totalsSection: {
    marginTop: 10,
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 3,
  },
  totalLabel: {
    width: "55%",
    textAlign: "right",
    paddingRight: 8,
    fontSize: 9,
    color: "#555",
  },
  totalValue: {
    width: "22%",
    textAlign: "right",
    fontSize: 9,
    color: "#333",
  },
  netRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    borderTopWidth: 1,
    borderTopColor: "#bbb",
    paddingTop: 7,
    marginTop: 4,
  },
  netLabel: {
    width: "55%",
    textAlign: "right",
    paddingRight: 8,
    fontFamily: "OpenSans-Bold",
    fontSize: 10,
  },
  netValue: {
    width: "22%",
    textAlign: "right",
    fontFamily: "OpenSans-Bold",
    fontSize: 10,
  },

  /* ── Payment section ── */
  paymentSection: {
    marginTop: 14,
  },
  paymentTitle: {
    fontFamily: "OpenSans-Bold",
    fontSize: 10,
    marginBottom: 6,
  },
  paymentRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  paymentLabel: {
    fontSize: 9,
    color: "#555",
  },
  paymentValue: {
    fontFamily: "OpenSans-Bold",
    fontSize: 9,
    color: "#111",
  },

  remainingRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#bbb",
    paddingTop: 6,
    marginTop: 6,
  },
  remainingLabel: {
    fontFamily: "OpenSans-Bold",
    fontSize: 10,
  },
  remainingValue: {
    fontFamily: "OpenSans-Bold",
    fontSize: 10,
  },

  /* ── Sample collection ── */
  sampleBox: {
    marginTop: 12,
    padding: 10,
    backgroundColor: "#eef8ff",
    borderLeftWidth: 4,
    borderLeftColor: "#2196f3",
    borderRadius: 2,
  },

  /* ── Instructions ── */
  instructionsBox: {
    marginTop: 10,
    padding: 8,
    backgroundColor: "#fffaf0",
    borderLeftWidth: 4,
    borderLeftColor: "#ff9800",
    borderRadius: 2,
  },

  /* ── Footer ── */
  footer: {
    marginTop: 14,
    borderTopWidth: 1,
    borderTopColor: "#e0e0e0",
    paddingTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 8.5,
    color: "#888",
  },
});

/* ---------------- Types ---------------- */
interface Service {
  srNo: number;
  description: string;
  qty: number;
  amount: number;
}

interface InvoicePdfProps {
  invoice: {
    clinicName?: string;
    clinicAddress?: {
      address?: string;
      streetAddress?: string;
      locality?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
    clinicPhone?: string;
    clinicEmail?: string;
    website?: string;
    gstNumber?: string;
    patientName?: string;
    patientId?: string;
    patientMobile?: string;
    doctorName?: string;
    doctorRegNo?: string;
    doctorSpeciality?: string;
    consultInvAmount?: number;
    receiptNo?: string;
    receiptDate?: string;
    labType?: string;
    clinicSubHeadingText?: string;
  };
  services: Service[];
  paymentDetails: {
    modeOfPayment?: string;
    cardType?: string;
    transactionNo?: string;
    bankName?: string;
  };
  printedBy?: string;
  printDateTime?: string;
  invoiceDate?: string;
  qrCodeData?: string;
  website?: string;
  totals?: {
    subtotal?: number;
    discountAmount?: number;
    taxAmount?: number;
    netPayable?: number;
    previouslyPaid?: number;
    amountReceivingNow?: number;
    remainingBalance?: number;
  };
  instructions?: string;
  labType?: string;
  sampleCollectionDetails?: {
    collectedBy?: string;
    sampleType?: string;
    barcodeId?: string;
    collectionDate?: string;
    collectionTime?: string;
    status?: string;
    expectedReportDate?: string;
  };
}

/* ---------------- INR Helper Component ---------------- */
/**
 * Renders the ₹ symbol using NotoSans (which has the glyph) and the
 * formatted amount in the parent font.  Use this everywhere instead of
 * the plain formatINR string helper so the rupee sign never renders as
 * a fallback character ("1") with OpenSans.
 */
const INR: React.FC<{ value?: number; style?: any }> = ({ value, style }) => {
  const amount =
    typeof value === "number"
      ? value.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  return (
    <Text style={style}>
      <Text style={{ fontFamily: "NotoSans" }}>{"\u20B9 "}</Text>
      {amount}
    </Text>
  );
};

/* ---------------- Component ---------------- */
const MedicalTestInvoicePdf: React.FC<InvoicePdfProps> = ({
  invoice = {} as any,
  services = [],
  paymentDetails = {},
  invoiceDate = "",
  website = "",
  totals = {},
}) => {
  const subtotal =
    totals.subtotal ??
    services.reduce((s, r) => s + (r.amount || 0) * (r.qty || 1), 0);
  const discountAmount = totals.discountAmount ?? 0;
  const taxAmount = totals.taxAmount ?? 0;
  const netPayable =
    totals.netPayable ?? subtotal - (discountAmount || 0) + (taxAmount || 0);
  const previouslyPaid = totals.previouslyPaid ?? 0;
  const amountReceivingNow = totals.amountReceivingNow ?? 0;
  const remainingBalance =
    totals.remainingBalance ??
    Math.max(0, netPayable - previouslyPaid - amountReceivingNow);

  // Build contact meta line
  const contactParts = [
    invoice?.clinicPhone && `Phone: ${invoice?.clinicPhone}`,
    invoice?.clinicEmail && `Email: ${invoice?.clinicEmail}`,
    website && `Website: ${website}`,
  ].filter(Boolean);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* ── Header (centered) ── */}
        <View style={styles.header} fixed>
          <Text style={styles.clinicTitle}>{invoice.clinicName ?? ""}</Text>

          {invoice?.clinicAddress && (
            <Text style={styles.clinicMeta}>
              {[
                invoice.clinicAddress.address,
                invoice.clinicAddress.streetAddress,
                invoice.clinicAddress.locality,
                invoice.clinicAddress.city,
                invoice.clinicAddress.state,
                invoice.clinicAddress.zipCode,
              ]
                .filter(Boolean)
                .join(", ")}
            </Text>
          )}
          {contactParts.length > 0 && (
            <Text style={styles.clinicMeta}>{contactParts.join(" | ")}</Text>
          )}
        </View>

        <View style={styles.divider} />

        {/* ── Receipt Number / Date ── */}
        <View style={styles.receiptRow}>
          <View style={{ flexDirection: "row" }}>
            <Text style={styles.receiptLabel}>Receipt Number: </Text>
            <Text style={styles.receiptValue}>{invoice.receiptNo ?? "-"}</Text>
          </View>
          <View style={{ flexDirection: "row" }}>
            <Text style={styles.receiptLabel}>Receipt Date: </Text>
            <Text style={styles.receiptValue}>
              {invoiceDate || (invoice as any).date || "-"}
            </Text>
          </View>
        </View>

        <View style={styles.thinDivider} />

        {/* ── Patient & Doctor ── */}
        <View style={styles.infoRow}>
          <View style={styles.infoCol}>
            <View style={styles.labelValueRow}>
              <Text style={styles.infoLabel}>Patient Name: </Text>
              <Text style={styles.infoValue}>{invoice.patientName ?? "-"}</Text>
            </View>
            <View style={styles.labelValueRow}>
              <Text style={styles.infoLabel}>Registration No: </Text>
              <Text style={styles.infoValue}>{invoice.patientId ?? "-"}</Text>
            </View>
          </View>
          <View style={styles.infoCol}>
            <View style={styles.labelValueRow}>
              <Text style={styles.infoLabel}>Doctor: </Text>
              <Text style={styles.infoValue}>{invoice.doctorName ?? "-"}</Text>
            </View>
            <View style={styles.labelValueRow}>
              <Text style={styles.infoLabel}>Contact: </Text>
              <Text style={styles.infoValue}>
                {invoice.patientMobile ?? "-"}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.thinDivider} />

        {/* ── Services table ── */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.colSrNo, styles.colHeaderText]}>#</Text>
            <Text style={[styles.colDesc, styles.colHeaderText]}>
              Service Description
            </Text>
            <Text style={[styles.colAmount, styles.colHeaderText]}>Amount</Text>
          </View>

          {services.map((svc) => (
            <View style={styles.tableRow} key={svc.srNo}>
              <Text style={styles.colSrNo}>{svc.srNo}</Text>
              <Text style={styles.colDesc}>{svc.description}</Text>
              {/* Use NotoSans for ₹ glyph, same as the fix applied elsewhere */}
              <Text style={styles.colAmount}>
                <Text style={{ fontFamily: "NotoSans" }}>{"\u20B9 "}</Text>
                {Math.round(svc.amount).toLocaleString("en-IN")}
              </Text>
            </View>
          ))}
        </View>

        {/* ── Totals ── */}
        <View style={styles.totalsSection}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>Subtotal</Text>
            <INR value={subtotal} style={styles.totalValue} />
          </View>

          {/* Discount – only shown when > 0 */}
          {discountAmount > 0 && (
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Discount</Text>
              <INR value={discountAmount} style={styles.totalValue} />
            </View>
          )}

          {/* Tax – only shown when > 0 */}
          {taxAmount > 0 && (
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Tax</Text>
              <INR value={taxAmount} style={styles.totalValue} />
            </View>
          )}

          <View style={styles.netRow}>
            <Text style={styles.netLabel}>Net Payable Amount</Text>
            <INR value={netPayable} style={styles.netValue} />
          </View>
        </View>

        {/* ── Payment Details ── */}
        <View style={styles.paymentSection}>
          <Text style={styles.paymentTitle}>Payment Details</Text>

          <View style={styles.paymentRow}>
            <Text style={styles.paymentLabel}>Mode of Payment</Text>
            <Text style={styles.paymentValue}>
              {paymentDetails.modeOfPayment ?? "-"}
            </Text>
          </View>

          <View style={styles.paymentRow}>
            <Text style={styles.paymentLabel}>Previously Paid</Text>
            <INR value={previouslyPaid} style={styles.paymentValue} />
          </View>

          <View style={styles.paymentRow}>
            <Text style={styles.paymentLabel}>Amount Received Now</Text>
            <INR value={amountReceivingNow} style={styles.paymentValue} />
          </View>

          {paymentDetails.transactionNo &&
            paymentDetails.transactionNo !== "-" && (
              <View style={styles.paymentRow}>
                <Text style={styles.paymentLabel}>Transaction ID</Text>
                <Text style={styles.paymentValue}>
                  {paymentDetails.transactionNo}
                </Text>
              </View>
            )}

          {paymentDetails.modeOfPayment &&
            paymentDetails.modeOfPayment.toUpperCase() !== "CASH" && (
              <>
                {paymentDetails.bankName && (
                  <View style={styles.paymentRow}>
                    <Text style={styles.paymentLabel}>Bank Name</Text>
                    <Text style={styles.paymentValue}>
                      {paymentDetails.bankName}
                    </Text>
                  </View>
                )}
                {paymentDetails.modeOfPayment?.toUpperCase() === "CARD" &&
                  paymentDetails.cardType && (
                    <View style={styles.paymentRow}>
                      <Text style={styles.paymentLabel}>Card Type</Text>
                      <Text style={styles.paymentValue}>
                        {paymentDetails.cardType}
                      </Text>
                    </View>
                  )}
              </>
            )}

          {/* Remaining Balance – clean bordered row */}
          <View style={styles.remainingRow}>
            <Text style={styles.remainingLabel}>Remaining Balance</Text>
            <INR value={remainingBalance} style={styles.remainingValue} />
          </View>
        </View>

        {/* ── Footer ── */}
        <View style={styles.footer}></View>
      </Page>
    </Document>
  );
};

export default MedicalTestInvoicePdf;
